import {
  type QueryClient,
  type QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useApi } from "@/core/api";
import { pollPayrollStatementStatus } from "@/payroll/pay-breakdown/payroll-statement-notifications/polling";
import type {
  PayrollStatementNotificationState,
  PayrollStatementProjectionBuild,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import {
  payrollStatementNotificationsKey,
  payrollStatementNotificationsPath,
  payrollStatementProjectionBuildKey,
  payrollStatementProjectionBuildKeyPrefix,
} from "./keys";
import { payrollStatementNotificationsQueryOptions } from "./notifications";

export const payrollStatementProjectionBuildQueryOptions = (
  api: AxiosInstance,
  businessId: string | null,
  periodStart: string | null,
  initial: PayrollStatementProjectionBuild | null | undefined,
) => ({
  queryKey: payrollStatementProjectionBuildKey(
    businessId,
    periodStart,
    initial?.generation_key ?? null,
  ),
  queryFn: ({ signal }: QueryFunctionContext) => {
    if (!businessId || !periodStart || !initial) {
      throw new Error("Projection build is required");
    }

    const path = `${payrollStatementNotificationsPath(
      businessId,
      periodStart,
    )}/projection_build`;
    return pollPayrollStatementStatus({
      initial,
      isPending: (build) => build.status === "pending",
      request: (requestSignal) =>
        api
          .get(path, { signal: requestSignal })
          .then(
            (res) =>
              res.data.projection_build as PayrollStatementProjectionBuild,
          ),
      signal,
    });
  },
  staleTime: Infinity,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
});

export const usePayrollStatementProjectionBuild = (
  businessId: string | null,
  periodStart: string | null,
  initial: PayrollStatementProjectionBuild | null | undefined,
) => {
  const api = useApi();

  const query = useQuery({
    ...payrollStatementProjectionBuildQueryOptions(
      api,
      businessId,
      periodStart,
      initial,
    ),
    enabled:
      !!businessId &&
      !!periodStart &&
      !!initial &&
      initial.status === "pending",
  });

  return { ...query, projectionBuild: query.data ?? initial ?? null };
};

export const useRetryPayrollStatementProjectionBuild = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    retryPayrollStatementProjectionBuildMutationOptions(
      api,
      queryClient,
      businessId,
      periodStart,
    ),
  );
};

export const retryPayrollStatementProjectionBuildMutationOptions = (
  api: AxiosInstance,
  queryClient: QueryClient,
  businessId: string | null,
  periodStart: string | null,
) => ({
  mutationFn: () => {
    if (!businessId || !periodStart) {
      throw new Error("Pay period is required");
    }

    return api
      .post(
        `${payrollStatementNotificationsPath(
          businessId,
          periodStart,
        )}/projection_build/retry`,
      )
      .then(
        (res) => res.data.projection_build as PayrollStatementProjectionBuild,
      );
  },
  onSuccess: async (projectionBuild: PayrollStatementProjectionBuild) => {
    queryClient.removeQueries({
      queryKey: payrollStatementProjectionBuildKeyPrefix(
        businessId,
        periodStart,
      ),
    });

    if (projectionBuild.status === "ready") {
      await queryClient.fetchQuery({
        ...payrollStatementNotificationsQueryOptions(
          api,
          businessId,
          periodStart,
        ),
        staleTime: 0,
      });
      return;
    }

    queryClient.setQueriesData<PayrollStatementNotificationState>(
      { queryKey: payrollStatementNotificationsKey(businessId, periodStart) },
      (state) =>
        state ? { ...state, projection_build: projectionBuild } : state,
    );
  },
});
